import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLanguageSync } from "@/hooks/useLanguage";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import JsonLd from "@/components/feature/JsonLd";
import { buildArticleSchema, buildBreadcrumbSchema } from "@/data/seo";
import { MAIN_SITE_URL } from "@/data/categories";
import { ARTICLES } from "@/data/articles";

const SECTIONS = ["s1", "s2", "s3"] as const;

export default function ArticleDetail() {
  const { lang = "en", slug = "" } = useParams();
  const { t } = useTranslation();
  useLanguageSync(lang);

  const article = ARTICLES.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return <Navigate to={`/${lang}`} replace />;
  }

  return (
    <>
      <JsonLd
        data={[
          buildArticleSchema({
            lang,
            path: `/${lang}/article/${slug}`,
            headline: t(`articles.${slug}.title`),
            description: t(`articles.${slug}.lead`),
            image: article.image,
          }),
          buildBreadcrumbSchema({
            lang,
            path: `/${lang}/article/${slug}`,
            name: t(`articles.${slug}.title`),
            homeLabel: t("article.breadcrumb"),
          }),
        ]}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar lang={lang} />
        <main className="pt-32 md:pt-40">
        <div className="w-full px-4 md:px-6 pb-16 md:pb-20">
          <article className="w-full max-w-3xl mx-auto">
            <nav className="text-xs text-foreground-500 flex items-center gap-2 flex-wrap">
              <Link to={`/${lang}`} className="hover:text-primary-600 transition-colors">
                {t("article.breadcrumb")}
              </Link>
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-s-line"></i>
              </span>
              <span>{t("nav.guide")}</span>
            </nav>

            <div className="relative w-full h-52 md:h-64 overflow-hidden rounded-lg mt-6">
              <img
                src={article.image}
                alt={t(`articles.${slug}.title`)}
                title={`${t(`articles.${slug}.title`)}`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <h1 className="mt-6 text-2xl md:text-4xl font-heading font-extrabold leading-tight">
              {t(`articles.${slug}.title`)}
            </h1>
            <p className="mt-4 text-sm text-foreground-500 flex items-center gap-2">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-time-line"></i>
              </span>
              {t("article.meta")}
            </p>

            <p className="mt-6 text-lg text-foreground-700 leading-relaxed">
              {t(`articles.${slug}.lead`)}
            </p>

            {SECTIONS.map((s) => (
              <section key={s} className="mt-10">
                <h2 className="text-xl md:text-2xl font-heading font-bold">
                  {t(`articles.${slug}.${s}t`)}
                </h2>
                <p className="mt-3 text-foreground-700 leading-relaxed">
                  {t(`articles.${slug}.${s}b`)}
                </p>
              </section>
            ))}

            <section className="mt-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold">
                {t("article.conclusion.title")}
              </h2>
              <p className="mt-3 text-foreground-700 leading-relaxed">
                {t(`articles.${slug}.conclusion`)}
              </p>
              <p className="mt-4 text-foreground-700 leading-relaxed">
                <Link to={`/${lang}`} className="text-primary-600 font-medium hover:underline">
                  {t("article.back")}
                </Link>
              </p>
            </section>

            <div className="mt-10 p-6 md:p-8 rounded-lg bg-primary-50 border border-primary-100">
              <h2 className="text-xl font-heading font-bold">{t("article.cta.title")}</h2>
              <div className="mt-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <a
                  href={MAIN_SITE_URL}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-external-link-line"></i>
                  {t("article.cta.button")}
                </a>
                <Link
                  to={`/${lang}`}
                  className="inline-flex items-center gap-2 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  {t("article.back")}
                </Link>
              </div>
            </div>
          </article>
        </div>
      </main>
      <Footer lang={lang} />
      </div>
    </>
  );
}